/**
 * The shared "soft status" colour shape, standardized across five call sites
 * that each independently improvised a `bg-X-100 dark:bg-X-900/40
 * text-X-800 dark:text-X-200 border border-X-300 dark:border-X-700` literal
 * in one of the now-retired raw Tailwind colour families:
 * `timer-card.js` (running/paused), `reminders.js` (pending/dispatched),
 * `inventory.js` (opened badge), and `product-master-form.js` (OFF
 * read-only note).
 *
 * Two token idioms already existed for this shape; this is the one that
 * survives measurement. `bg-danger/10 text-danger border border-danger/40`
 * (the idiom `timer-card.js`'s `elapsed` case used before it, too, was
 * pulled onto this module) puts `text-danger` directly on the dark card
 * with no `dark:` override — 2.02:1, far under the 4.5:1 floor — and its
 * `/10` fill separates from `surface.card-dark` at ~1.04:1, short of the
 * 1.18:1 plate floor used elsewhere in this file's test. `login.js`'s
 * auth-notice idiom (`bg-warning-soft text-warning dark:bg-warning/20
 * dark:text-warning-soft dark:ring-1 dark:ring-inset dark:ring-warning/40`)
 * clears both: the soft fill already carries ≥4.5:1 text in light mode
 * (pinned by `palette-contrast.test.mjs`'s "legible on its own soft fill"
 * case), and in dark mode `-soft` text on the translucent fill measures
 * >10:1.
 *
 * The colour is declared once and combined into different SHAPES below —
 * because the call sites do not share one layout. `STATUS_PILL_CLASS` is the
 * pill-shaped chip (`chip` + the tint) used by `timer-card.js`,
 * `reminders.js`, `inventory.js`; `STATUS_NOTE_CLASS` is the paragraph-shaped
 * banner used by `product-master-form.js`'s OFF read-only note, which cannot
 * carry `chip`'s `rounded-full` + inline-flex layout. `STATUS_TINT_CLASS`
 * itself is exported too — `login.js`'s auth-notice banner (`rounded-xl
 * px-3 py-2`, a third layout) composes it directly rather than repeating the
 * literal fill/text/ring fragment a third time. Baking `chip` into the one
 * exported pill constant is what let `product-master-form.js` drift into a
 * hand-copied literal instead of importing it — it had nothing importable
 * that fit its shape.
 */
export const STATUS_TINT_CLASS = {
  success: 'bg-success-soft text-success dark:bg-success/20 dark:text-success-soft dark:ring-1 dark:ring-inset dark:ring-success/40',
  warning: 'bg-warning-soft text-warning dark:bg-warning/20 dark:text-warning-soft dark:ring-1 dark:ring-inset dark:ring-warning/40',
  danger: 'bg-danger-soft text-danger dark:bg-danger/20 dark:text-danger-soft dark:ring-1 dark:ring-inset dark:ring-danger/40',
};

/** Pill/chip shape — `timer-card.js`, `reminders.js`, `inventory.js`. */
export const STATUS_PILL_CLASS = {
  success: `chip ${STATUS_TINT_CLASS.success}`,
  warning: `chip ${STATUS_TINT_CLASS.warning}`,
  danger: `chip ${STATUS_TINT_CLASS.danger}`,
};

/**
 * Paragraph-shaped note (`product-master-form.js`'s OFF read-only banner).
 * This is informational chrome, not a colour-coded status signal, so it keeps
 * the pre-conversion muted ink and its light-mode border rather than adopting
 * `STATUS_TINT_CLASS.warning`'s coloured text — that swap is a TONE change
 * this conversion does not call for, so only the fill/border move to tokens.
 *
 * The former raw values are deliberately NOT quoted here. Tailwind scans source
 * TEXT, not syntax: four amber classes named in this comment were emitted into
 * the shipped stylesheet purely by being written down, and they would trip the
 * raw-colour guard (`web/test/raw-colour-guard.test.mjs`) from inside a comment explaining their removal.
 */
export const STATUS_NOTE_CLASS = {
  warning: 'text-sm text-ink-muted bg-warning-soft border border-warning/30 dark:bg-warning/20 dark:border-warning/40 rounded-lg p-3',
};
